import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import BusinessSearchSelect from '../../components/BusinessSearchSelect';
import {
  getCatalogProducts, createCatalogProduct, updateCatalogProduct,
  getCatalogPackages, createCatalogPackage, updateCatalogPackage,
  getDiscountRules, createDiscountRule, updateDiscountRule, deleteDiscountRule, seedDiscountRules,
  calculateLicensePrice, calculateUpgradePrice,
  getLicenseOrders, createLicenseOrder, createLicenseUpgrade, cancelLicenseOrder,
  getLicenses,
} from '../../services/api';
import {
  DollarSign, Plus, Package, Tag, ShoppingCart, X, Edit2, Eye,
  CreditCard, Banknote, ArrowUpCircle, Percent, ChevronRight,
  Layers, ToggleLeft, ToggleRight, Download, Monitor, Globe, Check
} from 'lucide-react';
import { toast } from 'sonner';

// ==================== CONSTANTS ====================

const ORDER_STATUS_LABELS = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  confirmed: { label: 'Confirmado', color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completado', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

const ORDER_TYPE_LABELS = {
  new_sale: 'Nueva Venta',
  renewal: 'Renovacion',
  upgrade: 'Upgrade',
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Efectivo', icon: Banknote },
  { value: 'transfer', label: 'Transferencia (SPEI)', icon: ArrowUpCircle },
  { value: 'card', label: 'Tarjeta', icon: CreditCard },
  { value: 'mercadopago', label: 'Mercado Pago', icon: DollarSign },
];

const DURATION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// ==================== HELPER COMPONENTS ====================

const Modal = ({ open, onClose, title, children, size = 'md' }) => {
  if (!open) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-2xl w-full ${sizes[size]} max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
};

const Badge = ({ label, color }) => (
  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${color}`}>{label}</span>
);

const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
      active ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'text-slate-600 hover:bg-slate-50'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

const FormField = ({ label, children, required }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500";
const btnPrimary = "px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50";
const btnSecondary = "px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors";

// ==================== TAB 1: ORDERS ====================

const OrdersTab = ({ products }) => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ business_id: '', status: '', order_type: '', date_from: '', date_to: '' });
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await getLicenseOrders(params);
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Error al cargar ordenes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="w-64">
          <BusinessSearchSelect
            value={filters.business_id}
            onChange={(id) => setFilters(f => ({ ...f, business_id: id }))}
            placeholder="Filtrar por negocio..."
          />
        </div>
        <select className={inputCls + ' w-40'} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">Todos los estados</option>
          <option value="pending">Pendiente</option>
          <option value="completed">Completado</option>
          <option value="cancelled">Cancelado</option>
        </select>
        <select className={inputCls + ' w-40'} value={filters.order_type} onChange={e => setFilters(f => ({ ...f, order_type: e.target.value }))}>
          <option value="">Todos los tipos</option>
          <option value="new_sale">Nueva Venta</option>
          <option value="renewal">Renovacion</option>
          <option value="upgrade">Upgrade</option>
        </select>
        <input type="date" className={inputCls + ' w-40'} value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        <input type="date" className={inputCls + ' w-40'} value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setShowSaleModal(true)} className={btnPrimary}>
          <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Registrar Venta</span>
        </button>
        <button onClick={() => setShowUpgradeModal(true)} className={btnSecondary}>
          <span className="flex items-center gap-2"><ArrowUpCircle className="w-4 h-4" /> Upgrade</span>
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['#Orden', 'Negocio', 'Serial', 'Producto', 'Paquete', 'Duracion', 'Total', 'Pago', 'Status', 'Fecha', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">Sin ordenes</td></tr>
            ) : orders.map(o => (
              <tr key={o.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs">{o.order_number}</td>
                <td className="px-4 py-3 max-w-[150px] truncate">{o.business_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{o.serial_number}</td>
                <td className="px-4 py-3">{o.product_name}</td>
                <td className="px-4 py-3">{o.package_name}</td>
                <td className="px-4 py-3">{o.duration_months ? `${o.duration_months}m` : '-'}</td>
                <td className="px-4 py-3 font-semibold">${o.total_mxn?.toLocaleString()}</td>
                <td className="px-4 py-3 capitalize">{o.payment_method || '-'}</td>
                <td className="px-4 py-3">
                  <Badge {...(ORDER_STATUS_LABELS[o.status] || { label: o.status, color: 'bg-slate-100 text-slate-600' })} />
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{o.created_at?.substring(0, 10)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => setShowDetailModal(o)} className="p-1 hover:bg-slate-100 rounded"><Eye className="w-4 h-4 text-slate-400" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-2">{total} ordenes totales</p>

      {/* Modals */}
      {showSaleModal && <SaleModal products={products} onClose={() => setShowSaleModal(false)} onDone={() => { setShowSaleModal(false); loadOrders(); }} />}
      {showUpgradeModal && <UpgradeModal products={products} onClose={() => setShowUpgradeModal(false)} onDone={() => { setShowUpgradeModal(false); loadOrders(); }} />}
      {showDetailModal && <OrderDetailModal order={showDetailModal} onClose={() => setShowDetailModal(null)} />}
    </div>
  );
};

// ==================== SALE MODAL ====================

const SaleModal = ({ products, onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_id: '', business_name: '',
    product_id: '', license_id: '', package_id: '',
    modifier_details: [], duration_months: 1,
    payment_method: 'cash', payment_reference: '', notes: '',
  });
  const [packages, setPackages] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [priceCalc, setPriceCalc] = useState(null);

  // Load packages when product changes
  useEffect(() => {
    if (form.product_id) {
      getCatalogPackages({ product_id: form.product_id }).then(r => setPackages(r.data)).catch(() => {});
    }
  }, [form.product_id]);

  // Load licenses when business+product changes
  useEffect(() => {
    if (form.business_id && form.product_id) {
      const product = products.find(p => p.id === form.product_id);
      const posType = product?.legacy_pos_type;
      const params = { business_id: form.business_id };
      if (posType) params.pos_type = posType;
      getLicenses(params).then(r => setLicenses(r.data.licenses || [])).catch(() => {});
    }
  }, [form.business_id, form.product_id, products]);

  // When package changes, load modifiers and recalculate
  useEffect(() => {
    if (form.package_id) {
      const pkg = packages.find(p => p.id === form.package_id);
      setSelectedPackage(pkg);
      if (pkg) {
        const mods = (pkg.modifiers || []).map(m => ({
          code: m.code, label: m.label, extra_quantity: 0, price_per_unit: m.price_per_unit_mxn, line_total: 0,
        }));
        setForm(f => ({ ...f, modifier_details: mods }));
      }
    }
  }, [form.package_id, packages]);

  // Recalculate price whenever relevant fields change
  useEffect(() => {
    if (!form.package_id || !form.duration_months) return;
    const timer = setTimeout(async () => {
      try {
        const res = await calculateLicensePrice({
          package_id: form.package_id,
          duration_months: form.duration_months,
          modifier_details: form.modifier_details,
        });
        setPriceCalc(res.data);
      } catch { setPriceCalc(null); }
    }, 300);
    return () => clearTimeout(timer);
  }, [form.package_id, form.duration_months, form.modifier_details]);

  const updateModifier = (idx, qty) => {
    setForm(f => {
      const mods = [...f.modifier_details];
      mods[idx] = { ...mods[idx], extra_quantity: qty, line_total: qty * mods[idx].price_per_unit };
      return { ...f, modifier_details: mods };
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await createLicenseOrder({
        business_id: form.business_id,
        license_id: form.license_id || null,
        product_id: form.product_id,
        package_id: form.package_id,
        duration_months: form.duration_months,
        modifier_details: form.modifier_details,
        payment_method: form.payment_method,
        payment_reference: form.payment_reference || null,
        notes: form.notes || null,
      });
      toast.success('Venta registrada exitosamente');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar venta');
    } finally {
      setSaving(false);
    }
  };

  const canNext = () => {
    if (step === 1) return !!form.business_id;
    if (step === 2) return !!form.product_id;
    if (step === 3) return true; // license is optional
    if (step === 4) return !!form.package_id;
    if (step === 5) return true; // modifiers optional
    if (step === 6) return !!form.duration_months;
    if (step === 7) return !!form.payment_method;
    return true;
  };

  const totalSteps = 8;

  return (
    <Modal open onClose={onClose} title={`Registrar Venta (Paso ${step}/${totalSteps})`} size="lg">
      <div className="space-y-4">
        {/* Progress */}
        <div className="flex gap-1">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`h-1 flex-1 rounded ${i < step ? 'bg-teal-500' : 'bg-slate-200'}`} />
          ))}
        </div>

        {step === 1 && (
          <FormField label="Negocio" required>
            <BusinessSearchSelect
              value={form.business_id}
              onChange={(id, biz) => setForm(f => ({ ...f, business_id: id, business_name: biz?.name || '' }))}
            />
          </FormField>
        )}

        {step === 2 && (
          <FormField label="Producto de Software" required>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {products.filter(p => p.is_active).map(p => (
                <button key={p.id} onClick={() => setForm(f => ({ ...f, product_id: p.id }))}
                  className={`p-4 border rounded-xl text-left transition-all ${form.product_id === p.id ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="font-semibold text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{p.code}</div>
                  {p.is_installable && <Badge label="Instalable" color="bg-blue-50 text-blue-600" />}
                </button>
              ))}
            </div>
          </FormField>
        )}

        {step === 3 && (
          <FormField label="Licencia">
            <select className={inputCls} value={form.license_id} onChange={e => setForm(f => ({ ...f, license_id: e.target.value }))}>
              <option value="">Generar nueva licencia</option>
              {licenses.map(l => (
                <option key={l.id} value={l.id}>{l.serial_number} — {l.status}{l.valid_until ? ` (hasta ${l.valid_until.substring(0, 10)})` : ''}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">Selecciona una licencia existente o genera una nueva.</p>
          </FormField>
        )}

        {step === 4 && (
          <FormField label="Paquete/Plan" required>
            <div className="grid grid-cols-1 gap-3">
              {packages.filter(p => p.is_active).map(p => (
                <button key={p.id} onClick={() => setForm(f => ({ ...f, package_id: p.id }))}
                  className={`p-4 border rounded-xl text-left transition-all ${form.package_id === p.id ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900">{p.name}</div>
                    <div className="text-lg font-bold text-teal-700">${p.base_price_mxn}/mes</div>
                  </div>
                  {p.description && <p className="text-xs text-slate-500 mt-1">{p.description}</p>}
                  {p.features?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.features.map((f, i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{f}</span>)}
                    </div>
                  )}
                </button>
              ))}
              {packages.filter(p => p.is_active).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No hay paquetes disponibles para este producto</p>
              )}
            </div>
          </FormField>
        )}

        {step === 5 && (
          <div>
            <h4 className="font-medium text-slate-900 mb-3">Modificadores</h4>
            {form.modifier_details.length === 0 ? (
              <p className="text-sm text-slate-400">Este paquete no tiene modificadores.</p>
            ) : (
              <div className="space-y-3">
                {form.modifier_details.map((m, i) => {
                  const pkgMod = selectedPackage?.modifiers?.find(pm => pm.code === m.code);
                  return (
                    <div key={m.code} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">{m.label}</div>
                        <div className="text-xs text-slate-500">${m.price_per_unit}/unidad</div>
                      </div>
                      <input
                        type="number" min={pkgMod?.min_extra || 0} max={pkgMod?.max_extra || 999}
                        step={pkgMod?.step || 1} value={m.extra_quantity}
                        onChange={e => updateModifier(i, parseInt(e.target.value) || 0)}
                        className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm text-center"
                      />
                      <div className="w-24 text-right text-sm font-medium text-slate-700">
                        ${(m.extra_quantity * m.price_per_unit).toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === 6 && (
          <FormField label="Duracion (meses)" required>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {DURATION_OPTIONS.map(d => (
                <button key={d} onClick={() => setForm(f => ({ ...f, duration_months: d }))}
                  className={`py-3 rounded-lg text-sm font-medium border transition-all ${
                    form.duration_months === d ? 'border-teal-500 bg-teal-50 text-teal-700 ring-2 ring-teal-200' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {d} {d === 1 ? 'mes' : 'meses'}
                </button>
              ))}
            </div>
          </FormField>
        )}

        {step === 7 && (
          <div className="space-y-4">
            <FormField label="Metodo de Pago" required>
              <div className="grid grid-cols-2 gap-3">
                {PAYMENT_METHODS.map(pm => (
                  <button key={pm.value} onClick={() => setForm(f => ({ ...f, payment_method: pm.value }))}
                    className={`p-3 border rounded-xl flex items-center gap-3 transition-all ${
                      form.payment_method === pm.value ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <pm.icon className="w-5 h-5 text-slate-500" />
                    <span className="text-sm font-medium">{pm.label}</span>
                  </button>
                ))}
              </div>
            </FormField>
            <FormField label="Referencia de pago">
              <input className={inputCls} value={form.payment_reference} onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))} placeholder="Referencia o numero de transaccion" />
            </FormField>
          </div>
        )}

        {step === 8 && (
          <div className="space-y-4">
            <h4 className="font-semibold text-slate-900">Resumen de Venta</h4>
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm"><span>Negocio:</span><span className="font-medium">{form.business_name}</span></div>
              <div className="flex justify-between text-sm"><span>Producto:</span><span className="font-medium">{products.find(p => p.id === form.product_id)?.name}</span></div>
              <div className="flex justify-between text-sm"><span>Paquete:</span><span className="font-medium">{packages.find(p => p.id === form.package_id)?.name}</span></div>
              <div className="flex justify-between text-sm"><span>Duracion:</span><span className="font-medium">{form.duration_months} meses</span></div>
              <div className="flex justify-between text-sm"><span>Pago:</span><span className="font-medium capitalize">{form.payment_method}</span></div>
              <hr className="border-slate-200" />
              {priceCalc && (
                <>
                  <div className="flex justify-between text-sm"><span>Precio mensual:</span><span>${priceCalc.monthly_total?.toLocaleString()}</span></div>
                  <div className="flex justify-between text-sm"><span>Subtotal ({form.duration_months}m):</span><span>${priceCalc.subtotal?.toLocaleString()}</span></div>
                  {priceCalc.discount_amount > 0 && (
                    <div className="flex justify-between text-sm text-green-600"><span>Descuento ({priceCalc.discount_description}):</span><span>-${priceCalc.discount_amount?.toLocaleString()}</span></div>
                  )}
                  <div className="flex justify-between text-lg font-bold text-teal-700 pt-2 border-t border-slate-200"><span>TOTAL:</span><span>${priceCalc.total?.toLocaleString()} MXN</span></div>
                </>
              )}
            </div>
            <FormField label="Notas">
              <textarea className={inputCls} rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </FormField>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t border-slate-200">
          {step > 1 ? <button className={btnSecondary} onClick={() => setStep(s => s - 1)}>Anterior</button> : <div />}
          {step < totalSteps ? (
            <button className={btnPrimary} disabled={!canNext()} onClick={() => setStep(s => s + 1)}>
              <span className="flex items-center gap-1">Siguiente <ChevronRight className="w-4 h-4" /></span>
            </button>
          ) : (
            <button className={btnPrimary} disabled={saving} onClick={handleSubmit}>
              {saving ? 'Registrando...' : 'Confirmar Venta'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

// ==================== UPGRADE MODAL ====================

const UpgradeModal = ({ products, onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_id: '', license_id: '', new_package_id: '',
    payment_method: 'cash', payment_reference: '', notes: '',
  });
  const [licenses, setLicenses] = useState([]);
  const [selectedLic, setSelectedLic] = useState(null);
  const [packages, setPackages] = useState([]);
  const [upgradeCalc, setUpgradeCalc] = useState(null);

  // Load licenses
  useEffect(() => {
    if (form.business_id) {
      getLicenses({ business_id: form.business_id }).then(r => setLicenses(r.data.licenses || [])).catch(() => {});
    }
  }, [form.business_id]);

  // When license selected, load packages
  useEffect(() => {
    if (form.license_id) {
      const lic = licenses.find(l => l.id === form.license_id);
      setSelectedLic(lic);
      if (lic?.product_id) {
        getCatalogPackages({ product_id: lic.product_id }).then(r => setPackages(r.data)).catch(() => {});
      } else if (lic?.pos_type) {
        // Find product by legacy_pos_type
        getCatalogProducts({}).then(async (res) => {
          const prod = res.data.find(p => p.legacy_pos_type === lic.pos_type);
          if (prod) {
            const pkgRes = await getCatalogPackages({ product_id: prod.id });
            setPackages(pkgRes.data);
          }
        }).catch(() => {});
      }
    }
  }, [form.license_id, licenses]);

  // Calculate upgrade when package selected
  useEffect(() => {
    if (form.license_id && form.new_package_id) {
      calculateUpgradePrice({ license_id: form.license_id, new_package_id: form.new_package_id })
        .then(r => setUpgradeCalc(r.data))
        .catch(err => { toast.error(err.response?.data?.detail || 'Error de calculo'); setUpgradeCalc(null); });
    }
  }, [form.license_id, form.new_package_id]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await createLicenseUpgrade({
        license_id: form.license_id,
        new_package_id: form.new_package_id,
        payment_method: form.payment_method,
        payment_reference: form.payment_reference || null,
        notes: form.notes || null,
      });
      toast.success('Upgrade realizado exitosamente');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar upgrade');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Upgrade de Plan" size="lg">
      <div className="space-y-4">
        {step === 1 && (
          <>
            <FormField label="Negocio" required>
              <BusinessSearchSelect value={form.business_id} onChange={(id) => setForm(f => ({ ...f, business_id: id, license_id: '' }))} />
            </FormField>
            {form.business_id && (
              <FormField label="Licencia activa" required>
                <select className={inputCls} value={form.license_id} onChange={e => setForm(f => ({ ...f, license_id: e.target.value, new_package_id: '' }))}>
                  <option value="">Seleccionar licencia...</option>
                  {licenses.filter(l => ['activated', 'linked'].includes(l.status)).map(l => (
                    <option key={l.id} value={l.id}>{l.serial_number} — {l.pos_type} — hasta {l.valid_until?.substring(0, 10) || 'perpetua'}</option>
                  ))}
                </select>
              </FormField>
            )}
            {selectedLic && (
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <p><strong>Plan actual:</strong> {selectedLic.package_id ? packages.find(p => p.id === selectedLic.package_id)?.name || 'N/A' : 'Sin paquete asignado'}</p>
                <p><strong>Vigencia:</strong> hasta {selectedLic.valid_until?.substring(0, 10) || 'Perpetua'}</p>
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <FormField label="Nuevo Paquete" required>
            <div className="grid gap-3">
              {packages.filter(p => p.is_active).map(p => (
                <button key={p.id} onClick={() => setForm(f => ({ ...f, new_package_id: p.id }))}
                  className={`p-4 border rounded-xl text-left transition-all ${form.new_package_id === p.id ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="flex justify-between"><span className="font-semibold">{p.name}</span><span className="font-bold text-teal-700">${p.base_price_mxn}/mes</span></div>
                  <div className="text-xs text-slate-500 mt-1">Tier {p.tier}</div>
                </button>
              ))}
            </div>
          </FormField>
        )}

        {step === 3 && (
          <div className="space-y-4">
            {upgradeCalc && (
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm"><span>Plan actual:</span><span>{upgradeCalc.current_package_name || 'N/A'}</span></div>
                <div className="flex justify-between text-sm"><span>Costo mensual actual:</span><span>${upgradeCalc.current_monthly_cost}</span></div>
                <div className="flex justify-between text-sm"><span>Dias restantes:</span><span>{upgradeCalc.remaining_days}</span></div>
                <div className="flex justify-between text-sm text-green-600"><span>Credito prorrateado:</span><span>${upgradeCalc.prorated_credit}</span></div>
                <hr className="border-slate-200" />
                <div className="flex justify-between text-sm"><span>Nuevo plan:</span><span>{upgradeCalc.new_package_name}</span></div>
                <div className="flex justify-between text-sm"><span>Costo nuevo:</span><span>${upgradeCalc.new_monthly_cost}/mes</span></div>
                <div className="flex justify-between text-lg font-bold text-teal-700 pt-2 border-t border-slate-200"><span>A pagar:</span><span>${upgradeCalc.amount_to_pay} MXN</span></div>
              </div>
            )}
            <FormField label="Metodo de Pago" required>
              <div className="grid grid-cols-2 gap-3">
                {PAYMENT_METHODS.map(pm => (
                  <button key={pm.value} onClick={() => setForm(f => ({ ...f, payment_method: pm.value }))}
                    className={`p-3 border rounded-xl flex items-center gap-3 transition-all ${form.payment_method === pm.value ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <pm.icon className="w-5 h-5 text-slate-500" />
                    <span className="text-sm font-medium">{pm.label}</span>
                  </button>
                ))}
              </div>
            </FormField>
            <FormField label="Referencia de pago">
              <input className={inputCls} value={form.payment_reference} onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))} />
            </FormField>
          </div>
        )}

        <div className="flex justify-between pt-4 border-t border-slate-200">
          {step > 1 ? <button className={btnSecondary} onClick={() => setStep(s => s - 1)}>Anterior</button> : <div />}
          {step < 3 ? (
            <button className={btnPrimary} onClick={() => setStep(s => s + 1)} disabled={step === 1 ? !form.license_id : !form.new_package_id}>
              Siguiente
            </button>
          ) : (
            <button className={btnPrimary} disabled={saving || !upgradeCalc} onClick={handleSubmit}>
              {saving ? 'Procesando...' : 'Confirmar Upgrade'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

// ==================== ORDER DETAIL MODAL ====================

const OrderDetailModal = ({ order, onClose }) => (
  <Modal open onClose={onClose} title={`Orden ${order.order_number}`} size="md">
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <div><span className="text-slate-500">Tipo:</span> <strong>{ORDER_TYPE_LABELS[order.order_type] || order.order_type}</strong></div>
        <div><span className="text-slate-500">Status:</span> <Badge {...(ORDER_STATUS_LABELS[order.status] || { label: order.status, color: 'bg-slate-100' })} /></div>
        <div><span className="text-slate-500">Negocio:</span> <strong>{order.business_name}</strong></div>
        <div><span className="text-slate-500">Serial:</span> <code className="text-xs bg-slate-100 px-1 rounded">{order.serial_number}</code></div>
        <div><span className="text-slate-500">Producto:</span> <strong>{order.product_name}</strong></div>
        <div><span className="text-slate-500">Paquete:</span> <strong>{order.package_name}</strong></div>
        <div><span className="text-slate-500">Duracion:</span> <strong>{order.duration_months}m</strong></div>
        <div><span className="text-slate-500">Vigencia:</span> <strong>{order.valid_from?.substring(0, 10)} — {order.valid_until?.substring(0, 10)}</strong></div>
      </div>
      <hr className="border-slate-200" />
      <div className="bg-slate-50 rounded-lg p-3 space-y-1">
        <div className="flex justify-between"><span>Precio mensual:</span><span>${order.monthly_total}</span></div>
        <div className="flex justify-between"><span>Subtotal:</span><span>${order.subtotal}</span></div>
        {order.discount_amount > 0 && <div className="flex justify-between text-green-600"><span>Descuento ({order.discount_description}):</span><span>-${order.discount_amount}</span></div>}
        {order.prorated_credit > 0 && <div className="flex justify-between text-blue-600"><span>Credito upgrade ({order.remaining_days}d):</span><span>-${order.prorated_credit}</span></div>}
        <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total:</span><span>${order.total_mxn} MXN</span></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><span className="text-slate-500">Pago:</span> <strong className="capitalize">{order.payment_method || '-'}</strong></div>
        <div><span className="text-slate-500">Referencia:</span> <strong>{order.payment_reference || '-'}</strong></div>
      </div>
      {order.notes && <div><span className="text-slate-500">Notas:</span> <p className="text-slate-700">{order.notes}</p></div>}
    </div>
  </Modal>
);

// ==================== TAB 2: PRODUCTS ====================

const ProductsTab = ({ products, setProducts }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', code: '', description: '', is_installable: true,
    current_version: '', download_url: '', download_file_size_mb: '',
    release_notes: '', min_os_version: '', legacy_pos_type: '',
  });
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', code: '', description: '', is_installable: true, current_version: '', download_url: '', download_file_size_mb: '', release_notes: '', min_os_version: '', legacy_pos_type: '' });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '', code: p.code || '', description: p.description || '',
      is_installable: p.is_installable ?? true, current_version: p.current_version || '',
      download_url: p.download_url || '', download_file_size_mb: p.download_file_size_mb || '',
      release_notes: p.release_notes || '', min_os_version: p.min_os_version || '',
      legacy_pos_type: p.legacy_pos_type || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, download_file_size_mb: form.download_file_size_mb ? parseFloat(form.download_file_size_mb) : null };
      if (editing) {
        await updateCatalogProduct(editing.id, payload);
        toast.success('Producto actualizado');
      } else {
        await createCatalogProduct(payload);
        toast.success('Producto creado');
      }
      const res = await getCatalogProducts({ all: true });
      setProducts(res.data);
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar producto');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await updateCatalogProduct(p.id, { is_active: !p.is_active });
      const res = await getCatalogProducts({ all: true });
      setProducts(res.data);
      toast.success(p.is_active ? 'Producto desactivado' : 'Producto activado');
    } catch (err) {
      toast.error('Error al cambiar estado');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{products.length} productos</p>
        <button onClick={openNew} className={btnPrimary}><span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Nuevo Producto</span></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(p => (
          <div key={p.id} className={`border rounded-xl p-4 ${p.is_active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h4 className="font-semibold text-slate-900">{p.name}</h4>
                <p className="text-xs text-slate-500 font-mono">{p.code}</p>
              </div>
              <div className="flex gap-1">
                {p.is_installable && <Badge label="Instalable" color="bg-blue-50 text-blue-600" />}
                {!p.is_active && <Badge label="Inactivo" color="bg-red-50 text-red-600" />}
              </div>
            </div>
            {p.description && <p className="text-xs text-slate-500 mb-2">{p.description}</p>}
            <div className="text-xs text-slate-400 space-y-0.5">
              {p.current_version && <p>Version: {p.current_version}</p>}
              {p.download_url && <p className="truncate">URL: {p.download_url}</p>}
              {p.legacy_pos_type && <p>POS Type: {p.legacy_pos_type}</p>}
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
              <button onClick={() => openEdit(p)} className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"><Edit2 className="w-3 h-3" /> Editar</button>
              <button onClick={() => toggleActive(p)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                {p.is_active ? <><ToggleRight className="w-3 h-3" /> Desactivar</> : <><ToggleLeft className="w-3 h-3" /> Activar</>}
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? 'Editar Producto' : 'Nuevo Producto'}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Nombre" required><input className={inputCls} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></FormField>
            <FormField label="Codigo" required><input className={inputCls} value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} disabled={!!editing} /></FormField>
          </div>
          <FormField label="Descripcion"><textarea className={inputCls} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Version actual"><input className={inputCls} value={form.current_version} onChange={e => setForm(f => ({ ...f, current_version: e.target.value }))} /></FormField>
            <FormField label="Legacy POS Type">
              <select className={inputCls} value={form.legacy_pos_type} onChange={e => setForm(f => ({ ...f, legacy_pos_type: e.target.value }))}>
                <option value="">Ninguno</option>
                <option value="retail">Retail</option>
                <option value="restaurant">Restaurant</option>
                <option value="menu_digital">Menu Digital</option>
                <option value="servicios">Servicios</option>
              </select>
            </FormField>
          </div>
          <FormField label="URL de descarga"><input className={inputCls} value={form.download_url} onChange={e => setForm(f => ({ ...f, download_url: e.target.value }))} /></FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Tamano (MB)"><input className={inputCls} type="number" value={form.download_file_size_mb} onChange={e => setForm(f => ({ ...f, download_file_size_mb: e.target.value }))} /></FormField>
            <FormField label="OS Minimo"><input className={inputCls} value={form.min_os_version} onChange={e => setForm(f => ({ ...f, min_os_version: e.target.value }))} /></FormField>
          </div>
          <FormField label="Notas de version"><textarea className={inputCls} rows={2} value={form.release_notes} onChange={e => setForm(f => ({ ...f, release_notes: e.target.value }))} /></FormField>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_installable} onChange={e => setForm(f => ({ ...f, is_installable: e.target.checked }))} className="w-4 h-4 text-teal-600 rounded" />
            <span className="text-sm text-slate-700">Es instalable (descarga de escritorio)</span>
          </label>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button className={btnSecondary} onClick={() => setShowModal(false)}>Cancelar</button>
            <button className={btnPrimary} disabled={saving || !form.name || !form.code} onClick={handleSave}>{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ==================== TAB 3: PACKAGES ====================

const PackagesTab = ({ products }) => {
  const [packages, setPackages] = useState([]);
  const [filterProduct, setFilterProduct] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    product_id: '', name: '', code: '', description: '', tier: 1,
    base_price_mxn: 0, included_resources: [], modifiers: [], features: [],
    sort_order: 0,
  });
  const [featureInput, setFeatureInput] = useState('');

  const loadPackages = useCallback(async () => {
    try {
      const params = { all: true };
      if (filterProduct) params.product_id = filterProduct;
      const res = await getCatalogPackages(params);
      setPackages(res.data);
    } catch { toast.error('Error al cargar paquetes'); }
  }, [filterProduct]);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const openNew = () => {
    setEditing(null);
    setForm({ product_id: '', name: '', code: '', description: '', tier: 1, base_price_mxn: 0, included_resources: [], modifiers: [], features: [], sort_order: 0 });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      product_id: p.product_id, name: p.name, code: p.code, description: p.description || '',
      tier: p.tier, base_price_mxn: p.base_price_mxn,
      included_resources: p.included_resources || [], modifiers: p.modifiers || [],
      features: p.features || [], sort_order: p.sort_order || 0,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await updateCatalogPackage(editing.id, form);
        toast.success('Paquete actualizado');
      } else {
        await createCatalogPackage(form);
        toast.success('Paquete creado');
      }
      loadPackages();
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar paquete');
    } finally {
      setSaving(false);
    }
  };

  const addResource = () => setForm(f => ({ ...f, included_resources: [...f.included_resources, { code: '', name: '', quantity: 0, is_unlimited: false }] }));
  const updateResource = (i, field, val) => setForm(f => {
    const res = [...f.included_resources];
    res[i] = { ...res[i], [field]: val };
    return { ...f, included_resources: res };
  });
  const removeResource = (i) => setForm(f => ({ ...f, included_resources: f.included_resources.filter((_, idx) => idx !== i) }));

  const addModifier = () => setForm(f => ({ ...f, modifiers: [...f.modifiers, { code: '', label: '', price_per_unit_mxn: 0, step: 1, min_extra: 0, max_extra: 100 }] }));
  const updateModifier = (i, field, val) => setForm(f => {
    const mods = [...f.modifiers];
    mods[i] = { ...mods[i], [field]: val };
    return { ...f, modifiers: mods };
  });
  const removeModifier = (i) => setForm(f => ({ ...f, modifiers: f.modifiers.filter((_, idx) => idx !== i) }));

  const addFeature = () => {
    if (featureInput.trim()) {
      setForm(f => ({ ...f, features: [...f.features, featureInput.trim()] }));
      setFeatureInput('');
    }
  };
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  const toggleActive = async (p) => {
    try {
      await updateCatalogPackage(p.id, { is_active: !p.is_active });
      loadPackages();
      toast.success(p.is_active ? 'Paquete desactivado' : 'Paquete activado');
    } catch { toast.error('Error'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <select className={inputCls + ' w-48'} value={filterProduct} onChange={e => setFilterProduct(e.target.value)}>
          <option value="">Todos los productos</option>
          {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={openNew} className={btnPrimary}><span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Nuevo Paquete</span></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {packages.map(p => {
          const prod = products.find(pr => pr.id === p.product_id);
          return (
            <div key={p.id} className={`border rounded-xl p-4 ${p.is_active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-slate-900">{p.name}</h4>
                  <p className="text-xs text-slate-500">{prod?.name || p.product_id} — Tier {p.tier}</p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-teal-700">${p.base_price_mxn}</div>
                  <div className="text-xs text-slate-400">/mes</div>
                </div>
              </div>
              {p.included_resources?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {p.included_resources.map((r, i) => <span key={i} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded">{r.is_unlimited ? '∞' : r.quantity} {r.name}</span>)}
                </div>
              )}
              {p.modifiers?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {p.modifiers.map((m, i) => <span key={i} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">+{m.label} ${m.price_per_unit_mxn}/u</span>)}
                </div>
              )}
              {p.features?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {p.features.map((f, i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{f}</span>)}
                </div>
              )}
              {!p.is_active && <Badge label="Inactivo" color="bg-red-50 text-red-600" />}
              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                <button onClick={() => openEdit(p)} className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"><Edit2 className="w-3 h-3" /> Editar</button>
                <button onClick={() => toggleActive(p)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  {p.is_active ? <><ToggleRight className="w-3 h-3" /> Desactivar</> : <><ToggleLeft className="w-3 h-3" /> Activar</>}
                </button>
              </div>
            </div>
          );
        })}
        {packages.length === 0 && <p className="text-sm text-slate-400 col-span-2 text-center py-8">No hay paquetes</p>}
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? 'Editar Paquete' : 'Nuevo Paquete'} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Producto" required>
              <select className={inputCls} value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))} disabled={!!editing}>
                <option value="">Seleccionar...</option>
                {products.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </FormField>
            <FormField label="Tier"><input className={inputCls} type="number" min={1} value={form.tier} onChange={e => setForm(f => ({ ...f, tier: parseInt(e.target.value) || 1 }))} /></FormField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Nombre" required><input className={inputCls} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></FormField>
            <FormField label="Codigo" required><input className={inputCls} value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} disabled={!!editing} /></FormField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Precio base MXN/mes" required><input className={inputCls} type="number" min={0} step={0.01} value={form.base_price_mxn} onChange={e => setForm(f => ({ ...f, base_price_mxn: parseFloat(e.target.value) || 0 }))} /></FormField>
            <FormField label="Orden"><input className={inputCls} type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} /></FormField>
          </div>
          <FormField label="Descripcion"><textarea className={inputCls} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></FormField>

          {/* Menu Digital preset button */}
          {(() => {
            const selectedProduct = products.find(p => p.id === form.product_id);
            if (selectedProduct?.legacy_pos_type === 'menu_digital' && form.included_resources.length === 0 && form.modifiers.length === 0) {
              return (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-teal-800 mb-1">Producto de Menu Digital detectado</p>
                  <p className="text-xs text-teal-600 mb-3">Preconfigura los recursos y modificadores necesarios para controlar la cuota de ventas mensuales y el cobro por ventas extra.</p>
                  <button type="button" onClick={() => setForm(f => ({
                    ...f,
                    included_resources: [{ code: 'sales', name: 'Ventas mensuales', quantity: 50, is_unlimited: false }],
                    modifiers: [{ code: 'extra_sales', label: 'Venta extra', price_per_unit_mxn: 5.00, step: 1, min_extra: 0, max_extra: 999 }],
                  }))} className="text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-lg">
                    Preconfigurar para Menu Digital
                  </button>
                </div>
              );
            }
            return null;
          })()}

          {/* Resources */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Recursos incluidos</span>
              <button type="button" onClick={addResource} className="text-xs text-teal-600 hover:text-teal-700">+ Agregar</button>
            </div>
            {form.included_resources.map((r, i) => (
              <div key={i} className="flex gap-2 mb-2 items-center">
                <input className={inputCls + ' flex-1'} placeholder="Codigo" value={r.code} onChange={e => updateResource(i, 'code', e.target.value)} />
                <input className={inputCls + ' flex-1'} placeholder="Nombre" value={r.name} onChange={e => updateResource(i, 'name', e.target.value)} />
                <input className={inputCls + ' w-20'} type="number" placeholder="Cant." value={r.quantity} onChange={e => updateResource(i, 'quantity', parseInt(e.target.value) || 0)} />
                <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                  <input type="checkbox" checked={r.is_unlimited} onChange={e => updateResource(i, 'is_unlimited', e.target.checked)} className="w-3 h-3" /> Ilimitado
                </label>
                <button onClick={() => removeResource(i)} className="p-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              </div>
            ))}
            {form.included_resources.some(r => r.code === 'sales') && (
              <p className="text-xs text-slate-400 mt-1">* <strong>sales</strong>: Cada pedido del menu digital descuenta 1 de esta cuota mensual</p>
            )}
          </div>

          {/* Modifiers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Modificadores</span>
              <button type="button" onClick={addModifier} className="text-xs text-teal-600 hover:text-teal-700">+ Agregar</button>
            </div>
            {form.modifiers.map((m, i) => (
              <div key={i} className="flex gap-2 mb-2 items-center flex-wrap">
                <input className={inputCls + ' w-24'} placeholder="Codigo" value={m.code} onChange={e => updateModifier(i, 'code', e.target.value)} />
                <input className={inputCls + ' flex-1'} placeholder="Etiqueta" value={m.label} onChange={e => updateModifier(i, 'label', e.target.value)} />
                <input className={inputCls + ' w-24'} type="number" placeholder="$/u" value={m.price_per_unit_mxn} onChange={e => updateModifier(i, 'price_per_unit_mxn', parseFloat(e.target.value) || 0)} />
                <input className={inputCls + ' w-16'} type="number" placeholder="Step" value={m.step} onChange={e => updateModifier(i, 'step', parseInt(e.target.value) || 1)} />
                <input className={inputCls + ' w-16'} type="number" placeholder="Min" value={m.min_extra} onChange={e => updateModifier(i, 'min_extra', parseInt(e.target.value) || 0)} />
                <input className={inputCls + ' w-16'} type="number" placeholder="Max" value={m.max_extra} onChange={e => updateModifier(i, 'max_extra', parseInt(e.target.value) || 100)} />
                <button onClick={() => removeModifier(i)} className="p-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              </div>
            ))}
            {form.modifiers.some(m => m.code === 'extra_sales') && (
              <p className="text-xs text-slate-400 mt-1">* <strong>extra_sales</strong>: Precio que se cobra al negocio por cada venta que exceda la cuota mensual</p>
            )}
          </div>

          {/* Features */}
          <div>
            <span className="text-sm font-medium text-slate-700 mb-2 block">Caracteristicas</span>
            <div className="flex gap-2 mb-2">
              <input className={inputCls + ' flex-1'} placeholder="Agregar caracteristica..." value={featureInput} onChange={e => setFeatureInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }} />
              <button type="button" onClick={addFeature} className={btnSecondary}>+</button>
            </div>
            <div className="flex flex-wrap gap-1">
              {form.features.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                  {f} <button onClick={() => removeFeature(i)} className="text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button className={btnSecondary} onClick={() => setShowModal(false)}>Cancelar</button>
            <button className={btnPrimary} disabled={saving || !form.name || !form.code || !form.product_id} onClick={handleSave}>{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ==================== TAB 4: DISCOUNTS ====================

const DiscountsTab = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', code: '', duration_months: 6, discount_type: 'percentage',
    discount_value: 10, applies_to: 'all', is_active: true,
  });

  const loadDiscounts = async () => {
    try {
      setLoading(true);
      const res = await getDiscountRules();
      setDiscounts(res.data);
    } catch { toast.error('Error al cargar descuentos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadDiscounts(); }, []);

  const handleSeed = async () => {
    try {
      const res = await seedDiscountRules();
      toast.success(res.data.message);
      loadDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear descuentos iniciales');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await createDiscountRule(form);
      toast.success('Descuento creado');
      loadDiscounts();
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (d) => {
    try {
      if (d.is_active) {
        await deleteDiscountRule(d.id);
      } else {
        await updateDiscountRule(d.id, { is_active: true });
      }
      loadDiscounts();
      toast.success('Estado actualizado');
    } catch { toast.error('Error'); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button onClick={() => { setForm({ name: '', code: '', duration_months: 6, discount_type: 'percentage', discount_value: 10, applies_to: 'all', is_active: true }); setShowModal(true); }} className={btnPrimary}>
          <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Nuevo Descuento</span>
        </button>
        <button onClick={handleSeed} className={btnSecondary}>
          <span className="flex items-center gap-2"><Percent className="w-4 h-4" /> Seed Descuentos Iniciales</span>
        </button>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Nombre', 'Codigo', 'Meses', 'Tipo', 'Valor', 'Aplica a', 'Estado', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
            ) : discounts.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Sin descuentos</td></tr>
            ) : discounts.map(d => (
              <tr key={d.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{d.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.code}</td>
                <td className="px-4 py-3">{d.duration_months}</td>
                <td className="px-4 py-3 capitalize">{d.discount_type === 'percentage' ? 'Porcentaje' : 'Monto fijo'}</td>
                <td className="px-4 py-3 font-semibold">{d.discount_type === 'percentage' ? `${d.discount_value}%` : `$${d.discount_value}`}</td>
                <td className="px-4 py-3 capitalize">{d.applies_to === 'all' ? 'Todos' : 'Especificos'}</td>
                <td className="px-4 py-3">
                  <Badge label={d.is_active ? 'Activo' : 'Inactivo'} color={d.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} />
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => handleToggle(d)} className="text-xs text-slate-500 hover:text-slate-700">
                    {d.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Nuevo Descuento">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Nombre" required><input className={inputCls} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></FormField>
            <FormField label="Codigo" required><input className={inputCls} value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} /></FormField>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Meses minimos" required><input className={inputCls} type="number" min={1} max={12} value={form.duration_months} onChange={e => setForm(f => ({ ...f, duration_months: parseInt(e.target.value) || 1 }))} /></FormField>
            <FormField label="Tipo" required>
              <select className={inputCls} value={form.discount_type} onChange={e => setForm(f => ({ ...f, discount_type: e.target.value }))}>
                <option value="percentage">Porcentaje</option>
                <option value="fixed_amount">Monto fijo</option>
              </select>
            </FormField>
            <FormField label="Valor" required><input className={inputCls} type="number" min={0} step={0.01} value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: parseFloat(e.target.value) || 0 }))} /></FormField>
          </div>
          <FormField label="Aplica a">
            <select className={inputCls} value={form.applies_to} onChange={e => setForm(f => ({ ...f, applies_to: e.target.value }))}>
              <option value="all">Todos los paquetes</option>
              <option value="specific_packages">Paquetes especificos</option>
            </select>
          </FormField>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button className={btnSecondary} onClick={() => setShowModal(false)}>Cancelar</button>
            <button className={btnPrimary} disabled={saving || !form.name || !form.code} onClick={handleSave}>{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ==================== MAIN PAGE ====================

const VentasLicencias = () => {
  const [activeTab, setActiveTab] = useState('orders');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getCatalogProducts({ all: true }).then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const tabs = [
    { key: 'orders', icon: ShoppingCart, label: 'Ordenes / Ventas' },
    { key: 'products', icon: Monitor, label: 'Productos de Software' },
    { key: 'packages', icon: Package, label: 'Paquetes / Planes' },
    { key: 'discounts', icon: Percent, label: 'Descuentos' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-7 h-7 text-teal-600" />
              Ventas de Licencias
            </h1>
            <p className="text-sm text-slate-500 mt-1">Gestiona productos, paquetes, descuentos y ventas de licencias</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {tabs.map(t => (
            <TabButton key={t.key} active={activeTab === t.key} icon={t.icon} label={t.label} onClick={() => setActiveTab(t.key)} />
          ))}
        </div>

        {/* Content */}
        {activeTab === 'orders' && <OrdersTab products={products} />}
        {activeTab === 'products' && <ProductsTab products={products} setProducts={setProducts} />}
        {activeTab === 'packages' && <PackagesTab products={products} />}
        {activeTab === 'discounts' && <DiscountsTab />}
      </div>
    </DashboardLayout>
  );
};

export default VentasLicencias;
